import { faker } from "@faker-js/faker";
import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpPost$CreateComment } from "kreate-server/commands/CreateComment/fetcher";
import React from "react";
import { MdSend } from "react-icons/md";

import { useField$Comment } from "./hooks";
import styles from "./index.module.scss";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import AvatarViewer from "@/modules/kreate-components/components/AvatarViewer";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import InlineErrorViewer from "@/modules/kreate-modals/modals/ModalEnableMinting$Prompt/components/InlineErrorViewer";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork | null | undefined;
  disabled?: boolean;
  revalidateEverything: () => Promise<void>;
};

export default function CommentComposer({
  className,
  style,
  artwork,
  disabled,
  revalidateEverything,
}: Props) {
  if (!artwork) {
    disabled = true;
  }

  const loginStatus = useLoginStatus();
  const { busy, withDefaultErrorHandler } = useBusyCount();
  const { showMessage } = useToast();
  const api = useField$Comment({ initialValue: "" });

  const handleSubmit = !artwork
    ? undefined
    : withDefaultErrorHandler(async () => {
        const commentContent = api.validateBeforeSubmitting();
        await httpPost$CreateComment("/api/v1/all-comments/create", {
          targetId: artwork.artworkId,
          type: "artwork",
          content: commentContent,
        });
        showMessage({ color: "success", title: "Comment sent." });
        await revalidateEverything();
        api.reset("");
      });

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row padding="24px 0" alignItems="flex-start" gap="16px">
        <AvatarViewer
          value={loginStatus?.loggedIn ? loginStatus.avatar : undefined}
          size="40px"
        />
        <Flex.Col flex="1 1 64px" minWidth="64px" gap="8px">
          <div className={styles.textareaContainer}>
            <textarea
              className={styles.textarea}
              value={api.draft}
              onChange={(event) => api.setDraft(event.target.value)}
              onBlur={() => api.validateAfterEditing()}
              onDoubleClick={(event) =>
                event.altKey &&
                !api.draft &&
                api.setDraft(faker.lorem.paragraphs())
              }
              disabled={disabled || busy}
              placeholder="Add a new comment for the Artist to see!"
            />
            <div className={styles.rightAdornment}>
              <ButtonTransparentV2
                disabled={disabled || busy || !handleSubmit || !api.value}
                onClick={handleSubmit}
              >
                <MdSend size="20px" />
              </ButtonTransparentV2>
            </div>
          </div>
          {api.error && !!api.draft ? (
            <InlineErrorViewer content={api.error.message} />
          ) : undefined}
        </Flex.Col>
      </Flex.Row>
    </div>
  );
}
